import React, { useMemo } from "react";

import "./LandingFooter.css";

import arrowPng from "../assets/footer/arrow.png";
import arrowWebp from "../assets/footer/arrow.webp";
import arrowAvif from "../assets/footer/arrow.avif";
import facebookPng from "../assets/footer/facebook.png";
import facebookWebp from "../assets/footer/facebook.webp";
import facebookAvif from "../assets/footer/facebook.avif";
import instagramPng from "../assets/footer/instagram.png";
import instagramWebp from "../assets/footer/instagram.webp";
import instagramAvif from "../assets/footer/instagram.avif";
import substackPng from "../assets/footer/substack.png";
import substackWebp from "../assets/footer/substack.webp";
import substackAvif from "../assets/footer/substack.avif";
import threadsPng from "../assets/footer/threads.png";
import threadsWebp from "../assets/footer/threads.webp";
import threadsAvif from "../assets/footer/threads.avif";
import twitterPng from "../assets/footer/twitter.png";
import twitterWebp from "../assets/footer/twitter.webp";
import twitterAvif from "../assets/footer/twitter.avif";
import firstFooterIconPng from "../assets/footer/first-icon-footer.png";
import firstFooterIconWebp from "../assets/footer/first-icon-footer.webp";
import firstFooterIconAvif from "../assets/footer/first-icon-footer.avif";
import secondFooterIconPng from "../assets/footer/second-icon-footer.png";
import secondFooterIconWebp from "../assets/footer/second-icon-footer.webp";
import secondFooterIconAvif from "../assets/footer/second-icon-footer.avif";
import youWithoutSufferingBookPng from "../assets/footer/you-without-suffering-book.png";
import youWithoutSufferingBookWebp from "../assets/footer/you-without-suffering-book.webp";
import youWithoutSufferingBookAvif from "../assets/footer/you-without-suffering-book.avif";

const landingAssets = {
  arrow: { avif: arrowAvif, webp: arrowWebp, png: arrowPng },
  facebookIcon: { avif: facebookAvif, webp: facebookWebp, png: facebookPng },
  instagramIcon: { avif: instagramAvif, webp: instagramWebp, png: instagramPng },
  substackIcon: { avif: substackAvif, webp: substackWebp, png: substackPng },
  threadsIcon: { avif: threadsAvif, webp: threadsWebp, png: threadsPng },
  twitterIcon: { avif: twitterAvif, webp: twitterWebp, png: twitterPng },
  firstFooterIcon: { avif: firstFooterIconAvif, webp: firstFooterIconWebp, png: firstFooterIconPng },
  secondFooterIcon: { avif: secondFooterIconAvif, webp: secondFooterIconWebp, png: secondFooterIconPng },
  youWithoutSufferingBook: {
    avif: youWithoutSufferingBookAvif,
    webp: youWithoutSufferingBookWebp,
    png: youWithoutSufferingBookPng,
  },
};

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/GeneenRoth/",
    image: landingAssets.facebookIcon,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/geneenroth/?hl=en",
    image: landingAssets.instagramIcon,
  },
  {
    label: "Substack",
    href: "https://cookiesandconsciousness.substack.com/",
    image: landingAssets.substackIcon,
  },
  {
    label: "Threads",
    href: "https://www.threads.com/@geneenroth",
    image: landingAssets.threadsIcon,
  },
  {
    label: "X",
    href: "https://x.com/GeneenRoth?ref_src=twsrc%5Egoogle%7Ctwcamp%5Eserp%7Ctwgr%5Eauthor",
    image: landingAssets.twitterIcon,
  },
];

const CAPTCHA_BASE_URL =
  "https://email.geneenroth.com/email/admin/resources/form_designs/captcha/index.php?c=";

const EMAIL_REGEX =
  /[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;

const createSessionId = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let value = "";

  for (let i = 0; i < 32; i += 1) {
    value += letters[Math.floor(Math.random() * letters.length)];
  }

  return value;
};

function ResponsiveImage({
  sources,
  alt,
  className,
  loading = "lazy",
  decoding = "async",
  fetchPriority,
  ariaHidden,
  onError,
}) {
  const imgSrc = sources.webp ?? sources.png;

  const handleError = (event) => {
    if (event.currentTarget.src !== sources.png) {
      event.currentTarget.src = sources.png;
    }

    if (onError) {
      onError(event);
    }
  };

  return (
    <picture>
      <source srcSet={sources.avif} type="image/avif" />
      <source srcSet={sources.webp} type="image/webp" />
      <img
        className={className}
        src={imgSrc}
        alt={alt}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        aria-hidden={ariaHidden}
        onError={handleError}
      />
    </picture>
  );
}

export default function LandingFooter() {
  const captchaUrl = useMemo(() => {
    const randomValue = Math.floor(Math.random() * 1000) + 1;
    const sessionId = createSessionId();
    return `${CAPTCHA_BASE_URL}${randomValue}&ss=${sessionId}`;
  }, []);

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    const emailField = form.elements.namedItem("email");
    const firstNameField = form.elements.namedItem("CustomFields[12]");
    const captchaField = form.elements.namedItem("captcha");

    if (!(emailField instanceof HTMLInputElement) || !EMAIL_REGEX.test(emailField.value)) {
      event.preventDefault();
      alert("Please enter your email address.");
      emailField?.focus();
      return;
    }

    if (!(captchaField instanceof HTMLInputElement) || captchaField.value.trim() === "") {
      event.preventDefault();
      alert("Please enter the security code shown");
      captchaField?.focus();
      return;
    }

    if (!(firstNameField instanceof HTMLInputElement) || firstNameField.value.trim() === "") {
      event.preventDefault();
      alert("Please enter a value for field First Name");
      firstNameField?.focus();
    }
  };

  return (
    <footer className="landingFooter" aria-label="Footer">
      <div className="container landingFooterInner">
        <div className="landingFooterTop">
          <div className="landingFooterMeta">
            <a className="landingFooterMetaLink" href="https://geneenroth.com/terms-of-use/">
              <span className="landingFooterMetaBadge" aria-hidden>
                <ResponsiveImage
                  className="landingFooterMetaIconImage"
                  sources={landingAssets.firstFooterIcon}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  ariaHidden
                />
              </span>
              <span>Terms of Use</span>
            </a>
            <a className="landingFooterMetaLink" href="mailto:info@geneenroth.com">
              <span className="landingFooterMetaBadge" aria-hidden>
                <ResponsiveImage
                  className="landingFooterMetaIconImage"
                  sources={landingAssets.secondFooterIcon}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  ariaHidden
                />
              </span>
              <span>info@geneenroth.com</span>
            </a>
          </div>
          <div className="landingFooterSocials" aria-label="Geneen Roth social links">
            {SOCIAL_LINKS.map((link) => (
              <a
                key={link.label}
                className={`landingFooterSocialLink landingFooterSocialLink--${link.label.toLowerCase()}`}
                href={link.href}
                aria-label={link.label}
              >
                <ResponsiveImage
                  className="landingFooterSocialIcon"
                  sources={link.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  ariaHidden
                />
              </a>
            ))}
          </div>
        </div>

        <div className="landingFooterCta">
          <form
            className="landingFooterForm"
            method="post"
            action="https://email.geneenroth.com/email/form.php?form=34"
            id="frmSS34"
            onSubmit={handleSubmit}
          >
            <h2 className="landingFooterTitle">Join Geneen's Online Community Today!</h2>
            <div className="landingFooterFormRow">
              <input
                className="landingFooterInput"
                type="text"
                name="CustomFields[12]"
                id="CustomFields_12_34"
                placeholder="First Name"
                aria-label="First Name"
              />
              <input
                className="landingFooterInput"
                type="email"
                name="email"
                placeholder="Email"
                aria-label="Email"
              />
              <button className="landingFooterButton" type="submit">
                Join Now
              </button>
            </div>
            <input type="hidden" name="format" value="h" />
            <div className="landingFooterCaptcha">
              <img className="landingFooterCaptchaImage" src={captchaUrl} alt="Security code" />
              <input
                className="landingFooterInput landingFooterInput--captcha"
                type="text"
                name="captcha"
                placeholder="Security code"
                aria-label="Security code"
              />
            </div>
            <div className="landingFooterFormNote">
              <span>Free download when you join</span>
              <ResponsiveImage
                className="landingFooterArrow"
                sources={landingAssets.arrow}
                alt=""
                loading="lazy"
                decoding="async"
                ariaHidden
              />
            </div>
          </form>
          <div className="landingFooterBook" aria-hidden>
            <ResponsiveImage
              className="landingFooterBookImage"
              sources={landingAssets.youWithoutSufferingBook}
              alt=""
              loading="lazy"
              decoding="async"
              ariaHidden
            />
          </div>
        </div>
      </div>
    </footer>
  );
}
